import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { SegmentScheduleFrequency, SegmentScheduleResponse } from '../../../models/segment.model';
import { SegmentService } from '../../../services/segment-api.service';
import { ScheduledJobApiService } from '../../../../../shared/services/scheduled-job-api.service';
import { ScheduledJobExecutionResponse,
  scheduledJobExecutionStatusLabel,
} from '../../../../../shared/models/scheduled-job.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { DatetimePipe } from '../../../../../shared/pipe/datetime.pipe';

@Component({
  selector: 'app-segment-schedule-section',
  standalone: true,
  imports: [CommonModule, FormsModule, DatetimePipe],
  templateUrl: './segment-schedule-section.component.html',
})
export class SegmentScheduleSectionComponent implements OnChanges, OnDestroy {
  @Input() segmentId: number = 0;

  schedule: SegmentScheduleResponse | null = null;
  isScheduleModalOpen = false;
  isSavingSchedule = false;
  isTogglingSchedule = false;

  isHistoryOpen = false;
  isLoadingHistory = false;
  executions: ScheduledJobExecutionResponse[] = [];
  historyPage = 0;
  readonly historySize = 10;
  historyTotalElements = 0;
  historyTotalPages = 0;

  scheduleForm = {
    frecuency: 'DAILY' as SegmentScheduleFrequency,
    runTime: '08:00',
    runDays: [] as string[],
    runDaysMonthly: [] as number[],
    intervalValue: 5,
  };

  readonly weekDays = [
    { value: 'MON', label: 'Lun' },
    { value: 'TUE', label: 'Mar' },
    { value: 'WED', label: 'Mié' },
    { value: 'THU', label: 'Jue' },
    { value: 'FRI', label: 'Vie' },
    { value: 'SAT', label: 'Sáb' },
    { value: 'SUN', label: 'Dom' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private segmentApi: SegmentService,
    private scheduledJobApi: ScheduledJobApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  executionStatusLabel(status: string | null | undefined): string {
    return scheduledJobExecutionStatusLabel(status);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['segmentId'] && this.segmentId > 0) {
      this.reset();
      this.cargarSchedule();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.schedule = null;
    this.executions = [];
    this.historyPage = 0;
    this.isHistoryOpen = false;
  }

  cargarSchedule(): void {
    this.segmentApi.getSchedule(this.segmentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.schedule = res;
        if (this.isHistoryOpen && res?.scheduledJobConfigId) {
          this.cargarHistorial();
        }
      },
      error: (err) => {
        this.schedule = null;
        if (err?.status !== 404) {
          this.toast.error({ title: 'Error', message: 'No se pudo cargar la programación del segmento.' });
        }
      },
    });
  }

  openScheduleModal(): void {
    if (this.schedule) {
      const f = this.schedule.frecuency;
      this.scheduleForm.frecuency = f;
      this.scheduleForm.runTime = this.schedule.runTime ? this.schedule.runTime.substring(0, 5) : '08:00';
      this.scheduleForm.intervalValue = this.schedule.intervalValue ?? 5;
      this.scheduleForm.runDays = f === 'WEEKLY' && this.schedule.runDays
        ? this.schedule.runDays.map(d => String(d)) : [];
      this.scheduleForm.runDaysMonthly = f === 'MONTHLY' && this.schedule.runDays
        ? this.schedule.runDays.map(d => Number(d)) : [];
    } else {
      this.scheduleForm = { frecuency: 'DAILY', runTime: '08:00', runDays: [], runDaysMonthly: [], intervalValue: 5 };
    }
    this.isScheduleModalOpen = true;
  }

  closeScheduleModal(): void {
    this.isScheduleModalOpen = false;
  }

  toggleRunDay(day: string): void {
    const idx = this.scheduleForm.runDays.indexOf(day);
    if (idx === -1) {
      this.scheduleForm.runDays.push(day);
    } else {
      this.scheduleForm.runDays.splice(idx, 1);
    }
  }

  isRunDaySelected(day: string): boolean {
    return this.scheduleForm.runDays.includes(day);
  }

  get isSaveDisabled(): boolean {
    if (this.isSavingSchedule) return true;
    const f = this.scheduleForm.frecuency;
    if (f === 'MINUTES' || f === 'HOURLY') return this.scheduleForm.intervalValue < 1;
    if (f === 'WEEKLY') return this.scheduleForm.runDays.length === 0;
    if (f === 'MONTHLY') return this.scheduleForm.runDaysMonthly.length === 0;
    return false;
  }

  addMonthDay(input: HTMLInputElement): void {
    const day = parseInt(input.value, 10);
    if (!day || day < 1 || day > 31) return;
    if (!this.scheduleForm.runDaysMonthly.includes(day)) {
      this.scheduleForm.runDaysMonthly = [...this.scheduleForm.runDaysMonthly, day].sort((a, b) => a - b);
    }
    input.value = '';
  }

  removeMonthDay(day: number): void {
    this.scheduleForm.runDaysMonthly = this.scheduleForm.runDaysMonthly.filter(d => d !== day);
  }

  guardarSchedule(): void {
    this.isSavingSchedule = true;
    const f = this.scheduleForm.frecuency;
    const needsInterval = f === 'MINUTES' || f === 'HOURLY';
    const runTimeFormatted = this.scheduleForm.runTime.length === 5
      ? this.scheduleForm.runTime + ':00'
      : this.scheduleForm.runTime;

    const request = {
      segmentId: this.segmentId,
      frecuency: f,
      intervalValue: needsInterval ? this.scheduleForm.intervalValue : undefined,
      runTime: f !== 'MINUTES' ? runTimeFormatted : undefined,
      runDays: f === 'WEEKLY'
        ? this.scheduleForm.runDays
        : f === 'MONTHLY'
        ? this.scheduleForm.runDaysMonthly
        : undefined,
    };

    const obs = this.schedule
      ? this.segmentApi.updateSchedule(this.schedule.id, request)
      : this.segmentApi.createSchedule(request);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.schedule = res;
        this.isSavingSchedule = false;
        this.closeScheduleModal();
      },
      error: () => {
        this.isSavingSchedule = false;
        this.toast.error({ title: 'Error', message: 'No se pudo guardar la programación.' });
      },
    });
  }

  async eliminarSchedule(): Promise<void> {
    if (!this.schedule) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar programación',
      message: '¿Eliminar la programación de evaluación automática?',
    });
    if (!ok) return;

    this.segmentApi.deleteSchedule(this.schedule.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.schedule = null;
        this.executions = [];
        this.isHistoryOpen = false;
      },
      error: () => {
        this.toast.error({ title: 'Error', message: 'No se pudo eliminar la programación.' });
      },
    });
  }

  togglePauseSchedule(): void {
    if (!this.schedule || this.isTogglingSchedule) return;
    this.isTogglingSchedule = true;
    this.segmentApi.setScheduleEnabled(this.schedule.id, !this.schedule.enabled).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.schedule = res;
        this.isTogglingSchedule = false;
      },
      error: () => {
        this.isTogglingSchedule = false;
        this.toast.error({ title: 'Error', message: 'No se pudo cambiar el estado de la programación.' });
      },
    });
  }

  toggleHistoryPanel(): void {
    this.isHistoryOpen = !this.isHistoryOpen;
    if (this.isHistoryOpen && this.executions.length === 0 && this.schedule?.scheduledJobConfigId) {
      this.cargarHistorial();
    }
  }

  cargarHistorial(): void {
    const jobId = this.schedule?.scheduledJobConfigId;
    if (!jobId) return;
    this.isLoadingHistory = true;
    this.scheduledJobApi.getExecutions(jobId, this.historyPage, this.historySize).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.executions = res.content || [];
        this.historyTotalElements = res.totalElements || 0;
        this.historyTotalPages = res.totalPages || 0;
        this.isLoadingHistory = false;
      },
      error: () => {
        this.executions = [];
        this.isLoadingHistory = false;
        this.toast.error({ title: 'Error', message: 'No se pudo cargar el historial de ejecuciones.' });
      },
    });
  }

  prevHistoryPage(): void {
    if (this.historyPage > 0) {
      this.historyPage--;
      this.cargarHistorial();
    }
  }

  nextHistoryPage(): void {
    if (this.historyPage < this.historyTotalPages - 1) {
      this.historyPage++;
      this.cargarHistorial();
    }
  }

  getScheduleLabel(schedule: SegmentScheduleResponse): string {
    const iv = schedule.intervalValue ?? 1;
    const time = schedule.runTime ? schedule.runTime.substring(0, 5) : '—';
    switch (schedule.frecuency) {
      case 'MINUTES': return `Cada ${iv} minuto${iv === 1 ? '' : 's'}`;
      case 'HOURLY':  return `Cada ${iv} hora${iv === 1 ? '' : 's'}`;
      case 'DAILY':   return `Diaria a las ${time}`;
      case 'WEEKLY':  return `Semanal a las ${time}`;
      case 'MONTHLY': return `Mensual a las ${time}`;
      default:        return schedule.frecuency;
    }
  }

  formatRunDays(runDays: ReadonlyArray<string | number> | null | undefined): string {
    if (!runDays || runDays.length === 0) return '';
    const map: Record<string, string> = {
      MON: 'Lun', TUE: 'Mar', WED: 'Mié', THU: 'Jue', FRI: 'Vie', SAT: 'Sáb', SUN: 'Dom',
    };
    return (runDays as ReadonlyArray<string | number>).map(d => map[String(d)] ?? String(d)).join(', ');
  }

}
